import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, ArrowRight, FileUp, Loader2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

// Placeholder texts that will auto-type in the textarea placeholder
const placeholderTexts = [
  "A kanban board with drag-and-drop tasks....",
  "A blog with markdown support and comments....",
  "An e-commerce site with cart functionality....",
  "A portfolio with smooth animations....",
  "A dashboard with charts and analytics....",
  "Stuck on implementing user authentication....",
  "Need help with my React component structure....",
  "Want to add payment processing to my app....",
  "Need to integrate with Stripe....",
  "Want to add authentication to my app....",
  "Need to integrate with Firebase....",
  "Want to add payment processing to my app....",
  "Need to integrate with Stripe....",
  "Want to add authentication to my app....",
];

interface HeroInputProps {
  onSubmit: (description: string) => void;
}

const HeroInput = ({ onSubmit }: HeroInputProps) => {
  const [promptInput, setPromptInput] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentPlaceholder, setCurrentPlaceholder] = useState(placeholderTexts[0]);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-type effect for placeholder text
  useEffect(() => {
    const interval = setInterval(() => {
      const nextIndex = (placeholderIndex + 1) % placeholderTexts.length;
      setPlaceholderIndex(nextIndex);
      setCurrentPlaceholder(placeholderTexts[nextIndex]);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [placeholderIndex]);

  // Focus textarea on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promptInput.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(promptInput);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setIsUploading(true);
        setSelectedFile(file);
        
        // Simulate processing the image
        setTimeout(() => {
          setIsUploading(false);
          setPromptInput(prev => 
            prev + (prev ? '\n\n' : '') + 
            `Create an app based on the uploaded image called ${file.name.split('.')[0]}.`
          );
          toast({
            title: "Image processed",
            description: "We've analyzed your image and updated your prompt."
          });
        }, 1500);
      } else {
        toast({
          title: "Invalid file type",
          description: "Please upload an image file (PNG, JPG, etc.)",
          variant: "destructive"
        });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto">
      <div className="glass p-4 md:p-6 mb-4 animate-glow">
        <Textarea 
          ref={textareaRef}
          value={promptInput}
          onChange={(e) => setPromptInput(e.target.value)}
          placeholder={currentPlaceholder}
          className="min-h-[120px] bg-background/50 border-white/10 focus:border-primary text-base font-medium hero-textarea"
        />
        
        <div className="flex gap-3 mt-4">
          <Button 
            type="submit"
            className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-base flex items-center justify-center gap-2 h-12 hover:opacity-90 transition-opacity"
            disabled={isSubmitting || !promptInput.trim()}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5" />
                <span className="hidden sm:inline">Generate Perfect Prompts</span>
                <span className="sm:hidden">Generate</span>
              </>
            )}
          </Button>
          
          <div className="relative">
            <input 
              type="file" 
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              className="hidden"
            />
            <Button
              onClick={handleFileUpload}
              disabled={isUploading}
              variant="outline"
              className="bg-secondary/50 border-white/10 hover:bg-secondary h-12 w-12"
              aria-label="Upload image"
              title="Upload mockup or reference image"
            >
              {isUploading ? (
                <div className="h-5 w-5 rounded-full border-2 border-t-transparent border-white animate-spin"></div>
              ) : (
                <FileUp className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </div>
      
      <p className="text-sm text-muted-foreground text-center">
        Describe your app idea or where you're stuck, and we'll generate the perfect AI prompts
      </p>
    </form>
  );
};

export default HeroInput;
